import { useSelector } from 'react-redux';

import { isDevelopment } from '@core/blocks/edu-match/utils/determineEnv';
import { type Flags } from '@core/context/FeatureFlagsContext';
import { MonarchEditableAsset, NoPiiTestAsset } from '@core/context/types';
import { qualifyEduRequest } from '@core/services/leadDelivery';

import { type InjectionKey } from './getQuestionInjectionSchema';
import { type GCPParams, generateQualifyPayload, getQualifiedPartnerKeys } from './utils/qualifications';

type GNCPSParams = {
  flags: Flags;
  noPii: NoPiiTestAsset | false | 'conditions_not_met' | null | undefined;
  startTime?: number | null;
} & GCPParams;

export const isEditableAsset = (asset?: unknown) =>
  asset &&
  (asset as MonarchEditableAsset)?.questionStepId &&
  (asset as MonarchEditableAsset)?.questionStepName &&
  asset !== 'conditions_not_met';

const getInjectionKeys = async ({
  dcs,
  ipInferredPostalCode,
  flags,
  publisher,
  noPii,
  startTime,
}: GNCPSParams): Promise<InjectionKey[]> => {
  // Check for noPii variants
  // noPiiZipOnly = zip-only flow (name and contact info questions removed)
  // noPiiZipName = name + zip flow (contact info questions removed)
  // control = standard experience
  const isNoPiiObject = typeof noPii === 'object' && noPii !== null;
  const variant = isNoPiiObject && 'variant' in (noPii as Record<string, unknown>) ? (noPii as any).variant : null;
  const startDateValues =
    isNoPiiObject && 'startDateValues' in (noPii as Record<string, unknown>) ? (noPii as any).startDateValues : null;

  // Check if startTime is in the startDateValues array to enable the test
  const isTestEnabled =
    startDateValues && Array.isArray(startDateValues) && startTime !== undefined && startTime !== null
      ? startDateValues.includes(startTime)
      : false;

  if (noPii && variant === 'noPiiZipOnly' && isTestEnabled) {
    return ['noPiiZipOnly'];
  }

  if (noPii && variant === 'noPiiZipName' && isTestEnabled) {
    return ['noPiiZipName'];
  }

  // If the noPii test is present but NOT enabled for the selected start date,
  // proceed with normal partner injection logic (do not inject a fallback that hides PII)

  const qualifyPayload = generateQualifyPayload({ dcs, ipInferredPostalCode, publisher });

  const res = await qualifyEduRequest(qualifyPayload);

  if (res instanceof Error) {
    return [];
  }

  let injectionKeys = getQualifiedPartnerKeys(res.qualified, flags);

  Object.entries(flags).forEach(([key, value]) => {
    if (isEditableAsset(value)) injectionKeys.push(key as keyof Flags);
  });

  // Hardcoded rules
  if (injectionKeys.includes('EMCC')) {
    // Remove EAB keys if EMCC is present
    injectionKeys = injectionKeys.filter((key) => !key.includes('EAB'));
  }

  if (isDevelopment()) {
    console.log('Flags:', flags);
    console.log('Injection Keys:', injectionKeys);
  }

  return injectionKeys;
};

export default getInjectionKeys;
